import { toYMD } from './fechaLocal';
import { itemsAprobados } from './budgetItems';
import { parseKm } from './km';
import { comboDesdeProducto, ComboItem } from '../lib/comboItems';

/**
 * Lógica pura del alta de turno (pantalla "Nuevo turno"): renglones, duración,
 * horarios, etiquetas y el armado del payload que espera
 * POST /api/appointments-enhanced. Sin React ni llamadas a la API.
 */

export type TipoRenglon = 'service' | 'product';

export interface Renglon {
  id: string;
  name: string;
  qty: number;
  sellingPrice: number;
  type: TipoRenglon;
  /** minutos (solo servicios) */
  duration?: number;
  location?: string;
  stock?: number;
  /** estación del catálogo (products.workshopStationKey) */
  stationKey?: string | null;
  /** vino del presupuesto enlazado: se reemplaza si se cambia de presupuesto */
  fromBudget?: boolean;
  /** inclusiones de un combo ("Detailing T2"): viajan a la OT y de ahí al remito y la libreta */
  comboItems?: ComboItem[];
  requiredRole?: string;
  requiredSkills?: string[];
  requiredStaff?: number;
}

export type ModoCuando = '' | 'ahora' | 'hoy' | 'otro';

export interface SlotInfo {
  time: string;
  nivel: 'libre' | 'sobreturno' | 'lleno';
  ocupados: number;
  capacidad: number;
}

export interface Estacion {
  key: string;
  name: string;
}

export const horaHM = (d: Date = new Date()): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export const minutosDe = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/** "15:00" + 75 → "16:15" */
export const sumarMinutos = (hhmm: string, min: number): string => {
  const base = minutosDe(hhmm);
  if (base === null) return '';
  const t = (base + Math.max(0, Math.round(Number(min) || 0))) % (24 * 60);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

/** 75 → "1 h 15" · 45 → "45 min" · 120 → "2 h" */
export const fmtDuracion = (min: number): string => {
  const n = Math.max(0, Math.round(Number(min) || 0));
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (!h) return `${mm} min`;
  return mm ? `${h} h ${mm}` : `${h} h`;
};

export const plata = (v: number): string =>
  `$${Number(v || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

/**
 * Duración sugerida: suma de los servicios (mínimo 30). Sin renglones, 60.
 * Es una sugerencia: varios servicios comparten pasos (balanceo y desarme usan
 * el mismo sacar/poner rueda), así que se puede ajustar a mano.
 */
export const duracionSugerida = (renglones: Renglon[]): number => {
  if (renglones.length === 0) return 60;
  const total = renglones
    .filter((r) => r.type === 'service')
    .reduce((t, r) => t + (Number(r.duration) || 60) * (Number(r.qty) || 1), 0);
  return Math.max(total, 30);
};

export const totalRenglones = (renglones: Renglon[]): number =>
  renglones.reduce((t, r) => t + (Number(r.sellingPrice) || 0) * (Number(r.qty) || 0), 0);

/** Mismo id y tipo: suma la cantidad en vez de repetir el renglón */
export const agregarRenglon = (lista: Renglon[], nuevo: Renglon): Renglon[] => {
  const i = lista.findIndex((r) => r.id === nuevo.id && r.type === nuevo.type);
  if (i < 0) return [...lista, nuevo];
  const copia = lista.slice();
  copia[i] = { ...copia[i], qty: copia[i].qty + nuevo.qty };
  return copia;
};

export const cambiarCantidad = (lista: Renglon[], idx: number, qty: number): Renglon[] => {
  const n = Math.max(1, Math.round(Number(qty) || 1));
  return lista.map((r, i) => (i === idx ? { ...r, qty: n } : r));
};

export const quitarRenglon = (lista: Renglon[], idx: number): Renglon[] => lista.filter((_, i) => i !== idx);

const comboDe = (x: any): { comboItems?: ComboItem[] } => {
  const combo = comboDesdeProducto(x);
  return combo ? { comboItems: combo } : {};
};

export const renglonDeServicio = (s: any): Renglon => ({
  id: s.id,
  name: s.name,
  qty: 1,
  sellingPrice: Number(s.salePrice ?? s.sellingPrice ?? 0) || 0,
  type: 'service',
  duration: Number(s.duration) || 60,
  stationKey: s.workshopStationKey || null,
  requiredRole: s.requiredRole || '',
  requiredSkills: Array.isArray(s.requiredSkills) ? s.requiredSkills : [],
  requiredStaff: Number(s.requiredStaff) || 1,
  ...comboDe(s)
});

export const renglonDeProducto = (p: any): Renglon => ({
  id: p.id,
  name: p.name,
  qty: 1,
  sellingPrice: Number(p.salePrice ?? p.sellingPrice ?? 0) || 0,
  type: 'product',
  location: p.location || '',
  stock: typeof p.stock !== 'undefined' ? p.stock : p.currentStock,
  stationKey: p.workshopStationKey || null,
  ...comboDe(p)
});

/**
 * Renglones aprobados de un presupuesto (la base menos lo rechazado, más la
 * opción elegida). Si el ítem está en el catálogo se toman de ahí la duración
 * y la estación; el precio siempre es el del presupuesto.
 */
export const renglonesDePresupuesto = (
  presupuesto: any,
  catalogo: { servicios: any[]; productos: any[] } = { servicios: [], productos: [] }
): Renglon[] => {
  const items = Array.isArray(presupuesto?.items) ? presupuesto.items : [];
  const aprobados = itemsAprobados<any>(items, presupuesto?.selectedOption);
  const servicioPorId = new Map(catalogo.servicios.map((s) => [s.id, s]));
  const productoPorId = new Map(catalogo.productos.map((p) => [p.id, p]));
  return aprobados.map((it: any) => {
    const t = String(it.type || '').toLowerCase();
    const esServicio = t === 'service' || t === 'servicio';
    const id = it.productId || it.id || `tmp-${Math.random().toString(36).slice(2)}`;
    const base = esServicio
      ? (servicioPorId.get(id) ? renglonDeServicio(servicioPorId.get(id)) : null)
      : (productoPorId.get(id) ? renglonDeProducto(productoPorId.get(id)) : null);
    // Las inclusiones tildadas en el presupuesto mandan; si no las trae, las del catálogo
    const comboItems = Array.isArray(it.comboItems) && it.comboItems.length ? it.comboItems : base?.comboItems;
    return {
      ...(base || {}),
      id,
      name: it.description || it.name || base?.name || 'Ítem',
      qty: Number(it.quantity || it.qty || 1) || 1,
      sellingPrice: Number(it.sellingPrice ?? it.unitPrice ?? base?.sellingPrice ?? 0) || 0,
      type: esServicio ? 'service' : 'product',
      duration: esServicio ? (Number(it.duration) || base?.duration || 60) : undefined,
      ...(comboItems ? { comboItems } : {}),
      fromBudget: true
    } as Renglon;
  });
};

export const nombreCliente = (c: any): string =>
  c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || c?.name || 'Sin nombre';

export const etiquetaVehiculo = (v: any): string => {
  const modelo = [v?.make, v?.model, v?.year].filter(Boolean).join(' ');
  return `${v?.licensePlate || 'sin patente'}${modelo ? ` · ${modelo}` : ''}`;
};

const fechaDe = (ymd: string): Date => new Date(`${ymd}T12:00:00`);

/** "Hoy" · "Mañana" · "lunes 14/09" */
export const etiquetaDia = (ymd: string, hoy: string = toYMD(new Date())): string => {
  if (!ymd) return '';
  if (ymd === hoy) return 'Hoy';
  const manana = toYMD(new Date(fechaDe(hoy).getTime() + 24 * 60 * 60 * 1000));
  if (ymd === manana) return 'Mañana';
  return fechaDe(ymd).toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: '2-digit' });
};

export const esSlotPasado = (ymd: string, hhmm: string, ahora: Date = new Date()): boolean => {
  if (ymd !== toYMD(ahora)) return ymd < toYMD(ahora);
  const m = minutosDe(hhmm);
  return m !== null && m <= ahora.getHours() * 60 + ahora.getMinutes();
};

/** Para hoy se sacan los horarios que ya pasaron */
export const slotsVigentes = (slots: SlotInfo[], ymd: string, ahora: Date = new Date()): SlotInfo[] =>
  slots.filter((s) => !esSlotPasado(ymd, s.time, ahora));

/** Estaciones por las que pasa el trabajo, según el catálogo (solo informativo:
 *  la cola definitiva la arma el taller al recepcionar) */
export const estacionesDeRenglones = (renglones: Renglon[], estaciones: Estacion[]): Estacion[] => {
  const usadas = new Set(renglones.map((r) => r.stationKey).filter(Boolean) as string[]);
  return estaciones.filter((e) => usadas.has(e.key));
};

export interface ContactoOverride {
  name: string;
  phone: string;
}

export interface DatosTurno {
  clientId: string;
  vehicleId?: string;
  renglones: Renglon[];
  duracion: number;
  modo: ModoCuando;
  fecha: string;
  slot: string;
  ahora: Date;
  budgetId?: string | null;
  contacto?: ContactoOverride | null;
  notas?: string;
  km?: string;
  reclamo?: { es: boolean; sourceId?: string | null; tipo?: string; notas?: string };
}

/** Mensaje de error para mostrar, o null si se puede guardar */
export const validarTurno = (d: DatosTurno): string | null => {
  if (!d.clientId) return 'Elegí el cliente (o Consumidor final / Sin datos).';
  if (d.renglones.length === 0) return 'Cargá al menos un servicio o producto.';
  const dur = Math.round(Number(d.duracion));
  if (!Number.isFinite(dur) || dur < 15 || dur > 1440) return 'La duración tiene que estar entre 15 minutos y 24 horas.';
  if (!d.modo) return 'Elegí cuándo: ahora, hoy a una hora u otro día.';
  if (d.modo === 'otro' && !d.fecha) return 'Elegí la fecha del turno.';
  if (d.modo !== 'ahora' && !d.slot) return 'Elegí el horario.';
  if (d.modo === 'otro' && d.fecha < toYMD(d.ahora)) return 'La fecha ya pasó.';
  // El km NO es obligatorio al crear el turno, ni siquiera con "Ahora"
  // (21/09/2026). Si la OT sigue sin km, el taller lo pide al completar.
  if (d.reclamo?.es && !d.reclamo.tipo) return 'Indicá el tipo de reclamo.';
  return null;
};

/** Las inclusiones del combo viajan en cada renglón de la OT (el remito y la libreta las leen de ahí) */
const conCombo = (r: Renglon): { comboItems?: ComboItem[] } =>
  Array.isArray(r.comboItems) && r.comboItems.length ? { comboItems: r.comboItems } : {};

/** Cuerpo del POST /api/appointments-enhanced, igual al que mandaba el formulario anterior */
export const armarPayloadTurno = (d: DatosTurno): any => {
  const servicios = d.renglones.filter((r) => r.type === 'service');
  const productos = d.renglones.filter((r) => r.type === 'product');
  const ahora = d.modo === 'ahora';
  const fecha = ahora || d.modo === 'hoy' ? toYMD(d.ahora) : d.fecha;
  // Opcional al crear el turno; mismo parser que el resto del sistema ('84.200' → 84200)
  const km = parseKm(d.km);
  const payload: any = {
    clientId: d.clientId,
    ...(d.vehicleId ? { vehicleId: d.vehicleId } : {}),
    scheduledDate: fecha,
    timeSlot: ahora ? horaHM(d.ahora) : d.slot,
    status: ahora ? 'in_progress' : 'confirmed',
    relatedBudgetId: d.budgetId || null,
    estimatedDuration: Math.round(Number(d.duracion)),
    serviceType: servicios.map((s) => s.name).join(', ') || 'Servicio general',
    notes: d.notas || '',
    ...(km != null ? { mileage: km } : {}),
    oilChangeAndFilter: false,
    isReclamo: Boolean(d.reclamo?.es),
    reclamoSourceId: d.reclamo?.es ? d.reclamo.sourceId || null : null,
    reclamoType: d.reclamo?.es ? d.reclamo.tipo || null : null,
    reclamoNotes: d.reclamo?.es ? d.reclamo.notas || null : null,
    items: d.renglones.map((r) => ({
      name: r.name,
      description: r.name,
      quantity: r.qty,
      unitPrice: r.sellingPrice,
      totalPrice: r.sellingPrice * r.qty,
      type: r.type,
      ...conCombo(r)
    })),
    services: servicios.map((s) => ({
      id: s.id,
      name: s.name,
      quantity: s.qty,
      price: s.sellingPrice,
      duration: s.duration || 60,
      type: 'service',
      requiredRole: s.requiredRole || '',
      requiredSkills: s.requiredSkills || [],
      requiredStaff: s.requiredStaff || 1,
      ...conCombo(s)
    })),
    products: productos.map((p) => ({
      id: p.id,
      name: p.name,
      quantity: p.qty,
      price: p.sellingPrice,
      type: 'product',
      location: p.location || '',
      ...(typeof p.stock !== 'undefined' ? { stock: p.stock } : {}),
      ...conCombo(p)
    })),
    totalServiceCost: totalRenglones(d.renglones),
    resourceTypes: ['taller']
  };
  if (d.contacto?.phone) {
    payload.overrideContactName = d.contacto.name || '';
    payload.overrideContactPhone = d.contacto.phone;
  }
  return payload;
};
